<template>
  <div id="app">
    <q-layout view="hHh lpR fFf">
      <q-header v-if="!isLoginPage && !isMaintenanceMode" class="bg-primary">
        <HorizontalNavBar :user="user" :auth-expires-at="authExpiresAt" />
        <WarningBanner
          v-once
          v-if="showAlertBanner"
          :message="alertBannerMessage"
          :icon="alertBannerIcon"
          align="center"
        />
      </q-header>

      <q-drawer
        v-if="!isLoginPage && !isBuilder && !isMaintenanceMode"
        show-if-above
        elevated
        side="left"
        bordered
        mini-to-overlay
        class="vertical-bar"
        :breakpoint="500"
        :width="200"
        :mini="miniState"
        @mouseover="miniState = false"
        @mouseout="miniState = true"
      >
        <VerticalBar :mini="miniState" />
      </q-drawer>

      <q-page-container class="router-layout">
        <router-view />
      </q-page-container>
    </q-layout>
  </div>
</template>

<script lang="ts">
import { useDirectStore } from '@/utils/utils'
import { computed, defineComponent, ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import WarningBanner from './components/common/WarningBanner.vue'
import HorizontalNavBar from './components/NavBar/HorizontalNavBar.vue'
import VerticalBar from './components/NavBar/VerticalBar.vue'
import { ALERT_BANNER_ICON, ALERT_BANNER_MESSAGE, MAINTENANCE_MODE } from './config'
import { isProduction } from './environment'
import { isAlertBannerPeriod } from './utils/periods'

export default defineComponent({
  components: {
    HorizontalNavBar,
    VerticalBar,
    WarningBanner,
  },
  setup() {
    const { state, dispatch } = useDirectStore()
    const route = useRoute()

    const user = computed(() => state.auth.user)
    const authExpiresAt = computed(() => state.auth.authExpiresAt)
    const isLoginPage = computed(() => route.path === '/login')

    const isBuilder = computed(() => ['/rules/', '/policies/', '/user_functions/'].some(el => route.path.includes(el)))

    const miniState = ref(true)
    const token = localStorage.getItem('token')

    if (token) {
      dispatch.auth.UPDATE_AUTH_TOKEN(token)
    }

    const refreshToken = localStorage.getItem('refresh_token')
    if (refreshToken) {
      dispatch.auth.REFRESH_AUTH_TOKEN()
    }

    if (isProduction) {
      dispatch.appMeta.POLL_BUILD_INFO()
    }

    return {
      user,
      authExpiresAt,
      isLoginPage,
      miniState,
      isBuilder,
      isMaintenanceMode: MAINTENANCE_MODE,
      showAlertBanner: ALERT_BANNER_MESSAGE && isAlertBannerPeriod(),
      alertBannerMessage: ALERT_BANNER_MESSAGE,
      alertBannerIcon: ALERT_BANNER_ICON,
    }
  },
})
</script>

<style lang="scss">
@import '@/styles/quasar.variables.scss';
@import url('https://fonts.googleapis.com/css?family=Lato&display=swap');

#app {
  display: flex;
  flex-direction: column;
  color: $secondary;
  font-family: 'Lato', sans-serif;
}

.vertical-bar {
  background-color: $primary;
  color: $accent;
}

.router-layout {
  height: 100vh;
  display: flex;
}

.q-body--layout-animate .q-drawer__backdrop {
  transition: background-color 0.1s !important;
}
.q-drawer {
  transition:
    transform 0.1s,
    width 0.1s,
    top 0.1s,
    bottom 0.1s !important;
}
.q-layout__section--marginal {
  transition:
    transform 0.1s,
    left 0.1s,
    right 0.1s !important;
}
.q-page-container {
  transition:
    padding-top 0.1s,
    padding-right 0.1s,
    padding-bottom 0.1s,
    padding-left 0.1s !important;
}
.q-page-sticky {
  transition:
    transform 0.1s,
    left 0.1s,
    right 0.1s,
    top 0.1s,
    bottom 0.1s !important;
}
</style>
